import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Button, Space, message, Typography, Spin, Modal, InputNumber, Tag, Input, Tooltip } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined, FullscreenOutlined, TrophyOutlined, LinkOutlined, CopyOutlined } from '@ant-design/icons';
import { useWebSocket, WebSocketMessage } from '@/hooks/useWebSocket';
import { api } from '@/api';
import { Match, Participant } from '@/types/api.types';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { TournamentService } from '@/services/tournament.service';
import { compareMatchesForOrder } from '@/utils/matchOrder';
import styled from 'styled-components';

const { Title } = Typography;

const ControlContainer = styled.div`
  padding: 12px 16px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
`;

const TimerSection = styled.div`
  text-align: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 12px;
  color: white;
`;

const TimerDisplay = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  margin: 8px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const MatchInfoSection = styled.div`
  margin-top: 12px;
`;

const ParticipantInfo = styled.div`
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  margin: 4px 0;
  font-size: 14px;
`;

const NextMatchSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0f0f0;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
`;

const NextMatchTitle = styled.div`
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 2px;
  color: #666;
`;

const CategoryLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 6px;
  color: #888;
`;

const NextMatchInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

function getTatamiFromUrl(): number | null {
  const t = new URLSearchParams(window.location.search).get('tatami');
  if (t) {
    const n = parseInt(t, 10);
    if (!isNaN(n) && n >= 1) return n;
  }
  return null;
}

export const MatchBroadcastControl: React.FC = () => {
  const { id: tournamentId } = useParams<{ id: string }>();
  const [, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [currentMatch, setCurrentMatch] = useState<Match | null>(null);
  const [nextMatch, setNextMatch] = useState<Match | null>(null);
  const [timerSeconds, setTimerSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [selectedTatami, setSelectedTatami] = useState<number | null>(getTatamiFromUrl);
  const [isTatamiModalVisible, setIsTatamiModalVisible] = useState(false);
  const [updatingWinner, setUpdatingWinner] = useState(false);
  const [tournament, setTournament] = useState<{ streamLinks?: Record<string, string> | null } | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const broadcastWindowRef = useRef<Window | null>(null);

  const isUserOrganizer = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'head_manager';

  // Загрузка матчей
  const loadMatches = async () => {
    if (!tournamentId) return;
    
    try {
      const response = await api.get(`/tournaments/${tournamentId}/matches`);
      const matchesData = response.data?.data || response.data || [];
      
      // Фильтруем по выбранному татами, если он выбран
      const filteredMatches = selectedTatami 
        ? matchesData.filter((m: Match) => m.tatamiNumber === selectedTatami)
        : matchesData;
      
      setMatches(filteredMatches);
      
      // Сохраняем текущий выбранный матч, если он еще существует
      const currentMatchId = selectedMatchId;
      if (currentMatchId) {
        const updatedMatch = filteredMatches.find((m: Match) => m.id === currentMatchId);
        if (updatedMatch) {
          setCurrentMatch(updatedMatch);
        }
        // Матч с другого татами — не обновляем currentMatch здесь, сброс будет ниже
      }
      
      // Если матч не выбран или был удален / с другого татами, выбираем первый из live на этом татами
      if (!currentMatchId || !filteredMatches.find((m: Match) => m.id === currentMatchId)) {
        const liveMatches = filteredMatches.filter(
          (m: Match) => m.status === 'in_progress' || m.status === 'pending'
        );
        const liveSorted = [...liveMatches].sort((a, b) => {
          if (a.status === 'in_progress' && b.status === 'pending') return -1;
          if (a.status === 'pending' && b.status === 'in_progress') return 1;
          return compareMatchesForOrder(a, b);
        });
        
        if (liveSorted.length > 0) {
          const firstMatch = liveSorted[0];
          setSelectedMatchId(firstMatch.id || null);
          setCurrentMatch(firstMatch);
        } else {
          setSelectedMatchId(null);
          setCurrentMatch(null);
        }
      }
    } catch (error) {
      logger.error('Error loading matches:', error);
      if (!loading) {
        message.error('Ошибка загрузки матчей');
      }
    }
  };

  // Первоначальная загрузка
  useEffect(() => {
    const initialLoad = async () => {
      setLoading(true);
      await loadMatches();
      setLoading(false);
    };
    
    initialLoad();
  }, [tournamentId, selectedTatami]);

  // Загрузка турнира (streamLinks для ссылок на трансляции)
  useEffect(() => {
    const loadTournament = async () => {
      if (!tournamentId) return;
      try {
        const t = await TournamentService.getById(Number(tournamentId));
        setTournament(t);
      } catch (e) {
        logger.error('Error loading tournament for stream links:', e);
      }
    };
    loadTournament();
  }, [tournamentId]);

  // Автоматическое обновление матчей в реальном времени
  useEffect(() => {
    if (!tournamentId) return;
    
    // Обновляем каждые 3 секунды
    const interval = setInterval(() => {
      loadMatches();
    }, 3000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, selectedTatami]);

  // Обновление текущего матча и следующего при изменении выбора (как в Live Табло)
  useEffect(() => {
      const liveMatches = matches.filter(
        (m: Match) => m.status === 'in_progress' || m.status === 'pending'
      );
    const liveSorted = [...liveMatches].sort((a, b) => {
      if (a.status === 'in_progress' && b.status === 'pending') return -1;
      if (a.status === 'pending' && b.status === 'in_progress') return 1;
      return compareMatchesForOrder(a, b);
    });

    if (selectedMatchId) {
      const match = matches.find(m => m.id === selectedMatchId);
      setCurrentMatch(match || null);
      
      const currentIndex = liveSorted.findIndex(m => m.id === selectedMatchId);
      const next = currentIndex >= 0 && currentIndex < liveSorted.length - 1
        ? liveSorted[currentIndex + 1]
        : null;
      setNextMatch(next);
    } else if (liveSorted.length > 0) {
      const firstMatch = liveSorted[0];
      setSelectedMatchId(firstMatch.id || null);
      setCurrentMatch(firstMatch);
      
      const next = liveSorted.length > 1 ? liveSorted[1] : null;
      setNextMatch(next);
    } else {
      setNextMatch(null);
    }
  }, [selectedMatchId, matches]);

  // WebSocket для синхронизации (userId/role нужны бэкенду для проверки прав организатора)
  const { sendMessage } = useWebSocket({
    tournamentId: Number(tournamentId),
    userId: user?.id,
    role: user?.role,
    enabled: !!tournamentId && isUserOrganizer,
    onMessage: (message: WebSocketMessage) => {
      if (message.type === 'timer_control') {
        const { action, seconds } = message.data || {};
        
        if (action === 'start') {
          setIsRunning(true);
          setIsPaused(false);
          if (seconds !== undefined) {
            setTimerSeconds(seconds);
            startTimeRef.current = Date.now() - (seconds * 1000);
          } else {
            startTimeRef.current = Date.now() - (timerSeconds * 1000);
          }
        } else if (action === 'pause') {
          // Останавливаем интервал немедленно
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          setIsRunning(false);
          setIsPaused(true);
          // Обновляем время если передано
          if (seconds !== undefined) {
            setTimerSeconds(seconds);
          }
          // Сбрасываем startTimeRef чтобы предотвратить продолжение отсчета
          startTimeRef.current = null;
        } else if (action === 'reset') {
          setIsRunning(false);
          setIsPaused(false);
          setTimerSeconds(0);
          startTimeRef.current = null;
        } else if (action === 'update' && seconds !== undefined) {
          // При получении update от другого клиента (эхо нашего сообщения) игнорируем
          // чтобы избежать циклических обновлений
          // Организатор является источником истины, поэтому не синхронизируемся с другими
        }
      }
    },
  });

  // Логика секундомера
  useEffect(() => {
    // Очищаем интервал перед созданием нового
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (isRunning && !isPaused) {
      // Если startTimeRef не установлен, устанавливаем его на основе текущего времени
      if (startTimeRef.current === null) {
        const currentSeconds = timerSeconds || 0;
        startTimeRef.current = Date.now() - (currentSeconds * 1000);
      }
      
      let lastSentSeconds = -1;
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current !== null) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setTimerSeconds(elapsed);
          
          // Отправляем обновление каждую секунду для точной синхронизации
          if (elapsed !== lastSentSeconds) {
            lastSentSeconds = elapsed;
            try {
              sendMessage({
                type: 'timer_control',
                data: {
                  action: 'update',
                  seconds: elapsed,
                },
              });
            } catch (error) {
              logger.error('Error sending timer update:', error);
            }
          }
        }
      }, 100);            
    } else {
      // При паузе или остановке очищаем интервал
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;        
      }
      // При паузе сбрасываем startTimeRef
      if (!isRunning && isPaused) {
        startTimeRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isRunning, isPaused, sendMessage]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    // Устанавливаем startTimeRef перед отправкой сообщения
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now() - (timerSeconds * 1000);
    }
    
    setIsRunning(true);
    setIsPaused(false);
    
    // Отправляем сообщение с текущим значением секунд
    sendMessage({
      type: 'timer_control',
      data: {
        action: 'start',
        seconds: timerSeconds,
      },
    });
  };

  const handlePause = () => {
    // Останавливаем интервал немедленно - это критично для остановки отсчета
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Сбрасываем startTimeRef чтобы предотвратить продолжение отсчета
    startTimeRef.current = null;
    
    // Обновляем состояние
    setIsRunning(false);
    setIsPaused(true);
    
    // Отправляем сообщение о паузе с текущим значением секунд
    sendMessage({
      type: 'timer_control',
      data: {
        action: 'pause',
        seconds: timerSeconds, // Отправляем текущее значение секунд
      },
    }); 
  };

  const handleReset = () => {
    setIsRunning(false);
    setIsPaused(false);
    setTimerSeconds(0);
    startTimeRef.current = null;
    
    sendMessage({
      type: 'timer_control',
      data: {
        action: 'reset',
      },
    });
  };

  // Обработчик выбора победителя
  const handleWinnerSelect = async (participantId: number) => {
    if (!currentMatch || !tournamentId) return;
    
    if (updatingWinner) return;
    
    try {
      setUpdatingWinner(true);
      
      // Обновляем матч с победителем
      await TournamentService.updateMatchResult(Number(tournamentId), currentMatch.id, participantId);
      
      message.success('Победитель определен');
      
      // Задержка для того, чтобы сервер успел обновить следующий матч с победителем
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Перезагружаем матчи с обновленными данными
      const response = await api.get(`/tournaments/${tournamentId}/matches`);
      const matchesData = response.data?.data || response.data || [];
      const filteredMatches = selectedTatami 
        ? matchesData.filter((m: Match) => m.tatamiNumber === selectedTatami)
        : matchesData;
      setMatches(filteredMatches);
      
      // Находим текущий матч (который теперь completed)
      const completedCurrentMatch = filteredMatches.find((m: Match) => m.id === currentMatch.id);
      
      // Следующий бой в трансляции — всегда следующий по номеру на татами (matchNumber),
      // а не по nextMatchNumber из сетки (который ведёт в следующий раунд).
      const allSorted = [...filteredMatches].sort((a, b) => compareMatchesForOrder(a, b));
      const currentIndex = completedCurrentMatch
        ? allSorted.findIndex((m: Match) => m.id === completedCurrentMatch.id)
        : -1;
      const liveStatuses = ['in_progress', 'pending'];
      let nextMatch: Match | null = null;
      if (currentIndex >= 0) {
        for (let i = currentIndex + 1; i < allSorted.length; i++) {
          if (liveStatuses.includes(allSorted[i].status)) {
            nextMatch = allSorted[i];
            break;
          }
        }
      }
      if (!nextMatch) {
        const liveMatches = filteredMatches.filter(
          (m: Match) => m.status === 'in_progress' || m.status === 'pending'
        );
        const liveSorted = [...liveMatches].sort((a, b) => {
          if (a.status === 'in_progress' && b.status === 'pending') return -1;
          if (a.status === 'pending' && b.status === 'in_progress') return 1;
          return compareMatchesForOrder(a, b);
        });
        if (liveSorted.length > 0) nextMatch = liveSorted[0];
      }
      
      if (nextMatch) {
        // Обновляем matches перед установкой следующего матча
        setMatches(filteredMatches);
        
        // Устанавливаем новый текущий матч
        setSelectedMatchId(nextMatch.id || null);
        setCurrentMatch(nextMatch);
        setTimerSeconds(0);
        setIsRunning(false);
        setIsPaused(false);
        
        // Находим следующий после nextMatch
        const liveMatches = filteredMatches.filter(
          (m: Match) => m.status === 'in_progress' || m.status === 'pending'
        );
        const liveSorted = [...liveMatches].sort((a, b) => {
          if (a.status === 'in_progress' && b.status === 'pending') return -1;
          if (a.status === 'pending' && b.status === 'in_progress') return 1;
          return compareMatchesForOrder(a, b);
        });
        
        const nextMatchIndex = liveSorted.findIndex(m => m.id === nextMatch.id);
        const newNextMatch = nextMatchIndex >= 0 && nextMatchIndex < liveSorted.length - 1
          ? liveSorted[nextMatchIndex + 1]
          : null;
        
        setNextMatch(newNextMatch);
        
        // Отправляем WebSocket событие о смене матча с информацией о следующем
        sendMessage({
          type: 'timer_control',
          data: {
            action: 'match_changed',
            matchId: nextMatch.id,
            nextMatchId: newNextMatch?.id || null,
          },
        });       
                
        message.info(`Переход к следующему бою: ${getParticipantName(nextMatch.participant1 as Participant)} vs ${getParticipantName(nextMatch.participant2 as Participant)}`);
      } else {
        setMatches(filteredMatches);
        setNextMatch(null);
        setCurrentMatch(null);
        setSelectedMatchId(null);
        message.info('Все бои завершены');
      }
    } catch (error) {
      logger.error('Error updating winner:', error);
      message.error('Ошибка при определении победителя');
    } finally {
      setUpdatingWinner(false);
    }
  };

  const handleOpenBroadcast = (skipTatamiCheck = false, tatamiOverride?: number) => {
    const tatamiToUse = tatamiOverride ?? selectedTatami;
    
    // Если татами не выбран и не пропущена проверка, показываем модальное окно выбора
    if (!skipTatamiCheck && !tatamiToUse) {
      setIsTatamiModalVisible(true);
      return;
    }
    
    // Закрываем старое окно, если оно открыто, чтобы открыть новое с правильными параметрами
    if (broadcastWindowRef.current && !broadcastWindowRef.current.closed) {
      broadcastWindowRef.current.close();
      broadcastWindowRef.current = null;
    }
        
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = Date.now();
    const url = `${window.location.origin}/tournaments/${tournamentId}/broadcast${selectedMatchId ? `?matchId=${selectedMatchId}&tatami=${tatamiToUse}&t=${timestamp}` : `?tatami=${tatamiToUse}&t=${timestamp}`}`;
        
    // Параметры для открытия в режиме без меню и адресной строки
    // Примечание: современные браузеры ограничивают возможности window.open()
    // Для полного скрытия интерфейса лучше использовать полноэкранный режим (F11)
    const features = [
      'width=' + screen.width,
      'height=' + screen.height,
      'left=0',
      'top=0',
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'scrollbars=no',
      'resizable=yes',
      'titlebar=no',
      'directories=no'
    ].join(',');
    
    // Используем уникальное имя окна с татами и timestamp, чтобы каждое окно было уникальным
    const windowName = `broadcast-tatami-${tatamiToUse}-${Date.now()}`;
    const broadcastWindow = window.open(
      url,
      windowName,
      features
    );
    
    if (broadcastWindow) {
      broadcastWindowRef.current = broadcastWindow;
      
      // Пытаемся перевести в полноэкранный режим после загрузки
      const tryFullscreen = () => {
        try {
          // Для полноэкранного режима используем Fullscreen API
          if (broadcastWindow.document.documentElement.requestFullscreen) {
            broadcastWindow.document.documentElement.requestFullscreen();
          } else if ((broadcastWindow.document.documentElement as any).webkitRequestFullscreen) {
            (broadcastWindow.document.documentElement as any).webkitRequestFullscreen();
          } else if ((broadcastWindow.document.documentElement as any).mozRequestFullScreen) {
            (broadcastWindow.document.documentElement as any).mozRequestFullScreen();
          } else if ((broadcastWindow.document.documentElement as any).msRequestFullscreen) {
            (broadcastWindow.document.documentElement as any).msRequestFullscreen();
          }
        } catch (error) {
          logger.warn('Could not enter fullscreen mode:', error);
        }
      };
      
      // Пытаемся перевести в полноэкранный режим после загрузки
      if (broadcastWindow.document.readyState === 'complete') {
        setTimeout(tryFullscreen, 500);
      } else {
        broadcastWindow.addEventListener('load', () => {
          setTimeout(tryFullscreen, 500);
        });
      }
      
      // Также пытаемся максимизировать окно
      try {
        broadcastWindow.moveTo(0, 0);
        broadcastWindow.resizeTo(screen.width, screen.height);
      } catch (error) {
        logger.warn('Could not resize window:', error);
      }
      
      message.success('Окно трансляции открыто. Оно автоматически перейдет в полноэкранный режим.');
    } else {
      message.error('Не удалось открыть окно трансляции. Разрешите всплывающие окна.');
    }
  };

  const handleTatamiSelect = async () => {
    if (!selectedTatami) {
      message.warning('Выберите татами');
      return;
    }
    setIsTatamiModalVisible(false);
    
    const tatamiToUse = selectedTatami;
    
    try {
      const response = await api.get(`/tournaments/${tournamentId}/matches`);
      const matchesData = response.data?.data || response.data || [];
      const filteredMatches = matchesData.filter((m: Match) => m.tatamiNumber === tatamiToUse);
      setMatches(filteredMatches);
            
      const liveMatches = filteredMatches.filter(
        (m: Match) => m.status === 'in_progress' || m.status === 'pending'
      );
      const liveSorted = [...liveMatches].sort((a, b) => {
        if (a.status === 'in_progress' && b.status === 'pending') return -1;
        if (a.status === 'pending' && b.status === 'in_progress') return 1;
        return compareMatchesForOrder(a, b);
      });
      
      if (liveSorted.length > 0) {
        const firstMatch = liveSorted[0];
        setSelectedMatchId(firstMatch.id || null);
        setCurrentMatch(firstMatch);
      } else {
        setSelectedMatchId(null);
        setCurrentMatch(null);
      }
      
      setSelectedTatami(tatamiToUse);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('tatami', String(tatamiToUse));
        return next;
      });
    } catch (error) {
      logger.error('Error loading matches:', error);
      message.error('Ошибка загрузки матчей');
    }
  };

  // Загружаем список татами; при отсутствии татами в URL показываем модальное окно выбора
  useEffect(() => {
    const loadTatamis = async () => {
      if (!tournamentId) return;
      try {
        const response = await api.get(`/tournaments/${tournamentId}/matches`);
        const matchesData = response.data?.data || response.data || [];
        const tatamiSet = new Set<number>();
        matchesData.forEach((m: Match) => {
          if (m.tatamiNumber) {
            tatamiSet.add(m.tatamiNumber);
          }
        });
        const tatamis = Array.from(tatamiSet).sort((a, b) => a - b);
        const tatamiFromUrl = getTatamiFromUrl();
        if (tatamis.length > 0) {
          if (tatamiFromUrl !== null && tatamis.includes(tatamiFromUrl)) {
            setSelectedTatami(tatamiFromUrl);
          } else {
            setSelectedTatami(tatamis[0]);
            setIsTatamiModalVisible(true);
          }
        }
      } catch (error) {
        logger.error('Error loading tatamis:', error);
      }
    };
    loadTatamis();
  }, [tournamentId]);

  const getParticipantName = (participant: Participant | null | undefined): string => {
    if (!participant) return 'Ожидание участника';
    const firstName = participant.firstName || '';
    const lastName = participant.lastName || '';
    return `${lastName} ${firstName}`;
  };

  if (!isUserOrganizer) {
    return (
      <ControlContainer>
        <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
          <Title level={5} style={{ margin: 0 }}>Доступ запрещен</Title>
          <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>Только организаторы могут управлять трансляцией.</p>
        </Card>
      </ControlContainer>
    );
  }

  if (loading) {
    return (
      <ControlContainer>
        <Spin size="small" />
      </ControlContainer>
    );
  }

  return (
    <ControlContainer>
      <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>Управление трансляцией боя</Title>
          {selectedTatami != null && (
            <Button size="small" onClick={() => setIsTatamiModalVisible(true)}>
              Татами {selectedTatami}
            </Button>
          )}
        </div>
        
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <TimerSection>
            <div style={{ color: 'white', fontSize: 13, marginBottom: 4 }}>Секундомер</div>
            <TimerDisplay>{formatTime(timerSeconds)}</TimerDisplay>
            <Space size="small">
              {!isRunning ? (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={handleStart}
                >
                  Старт
                </Button>
              ) : (
                <Button
                  size="small"
                  icon={<PauseCircleOutlined />}
                  onClick={handlePause}
                >
                  Пауза
                </Button>
              )}
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleReset}
              >
                Сброс
              </Button>
            </Space>
          </TimerSection>

          {currentMatch && (
            <MatchInfoSection>
              <Card
                size="small"
                title={
                  <div style={{ fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Текущий матч</span>
                      <Tag color="blue" style={{ fontSize: 12, padding: '2px 8px' }}>
                        Бой #{(currentMatch.matchNumber ?? currentMatch.id)}
                      </Tag>
                    </div>
                    {currentMatch.category?.name && (
                      <CategoryLabel style={{ marginTop: 4, marginBottom: 0 }}>{currentMatch.category.name}</CategoryLabel>
                    )}
                  </div>
                }
                bodyStyle={{ padding: '8px 12px' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <ParticipantInfo style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 13 }}>{getParticipantName(currentMatch.participant1 as Participant)}</strong>
                    <Button
                      type="primary"
                      size="small"
                      icon={<TrophyOutlined />}
                      onClick={() => handleWinnerSelect((currentMatch.participant1 as Participant)?.id!)}
                      loading={updatingWinner}
                      disabled={!currentMatch.participant1 || currentMatch.status === 'completed'}
                    >
                      Победил
                    </Button>
                  </ParticipantInfo>
                  <div style={{ textAlign: 'center', margin: '4px 0', fontSize: 14 }}>VS</div>
                  <ParticipantInfo style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 13 }}>{getParticipantName(currentMatch.participant2 as Participant)}</strong>
                    <Button
                      type="primary"
                      size="small"
                      icon={<TrophyOutlined />}
                      onClick={() => handleWinnerSelect((currentMatch.participant2 as Participant)?.id!)}
                      loading={updatingWinner}
                      disabled={!currentMatch.participant2 || currentMatch.status === 'completed'}
                    >
                      Победил
                    </Button>
                  </ParticipantInfo>
                </Space>
              </Card>
            </MatchInfoSection>
          )}

          {nextMatch && (
            <NextMatchSection>
              <NextMatchTitle>Следующий бой</NextMatchTitle>
              {nextMatch.category?.name && (
                <CategoryLabel>{nextMatch.category.name}</CategoryLabel>
              )}
              <NextMatchInfo>
                <div>
                  <strong>{getParticipantName(nextMatch.participant1 as Participant)}</strong>
                </div>
                <div style={{ fontSize: 13, fontWeight: 'bold' }}>VS</div>
                <div>
                  <strong>{getParticipantName(nextMatch.participant2 as Participant)}</strong>
                </div>
                <div style={{ marginLeft: 'auto', color: '#666', fontSize: 12 }}>
                  Бой #{(nextMatch.matchNumber ?? nextMatch.id)}
                </div>
              </NextMatchInfo>
            </NextMatchSection>
          )}

          {selectedTatami && tournament?.streamLinks?.[String(selectedTatami)] && (
            <NextMatchSection>
              <NextMatchTitle>Ссылка на трансляцию (татами {selectedTatami})</NextMatchTitle>
              <div style={{ marginTop: 4 }}>
                <Button
                  type="link"
                  size="small"
                  href={tournament.streamLinks[String(selectedTatami)]}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<LinkOutlined />}
                  style={{ padding: 0 }}
                >
                  Открыть трансляцию (vkvideo.ru и др.)
                </Button>
              </div>
            </NextMatchSection>
          )}

          {selectedTatami && tournamentId && (
            <NextMatchSection>
              <NextMatchTitle>Ссылка для OBS (Browser Source)</NextMatchTitle>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Input
                  readOnly
                  value={`${window.location.origin}/tournaments/${tournamentId}/broadcast?tatami=${selectedTatami}`}
                  style={{ flex: 1, minWidth: 200, fontFamily: 'monospace', fontSize: 12 }}
                />
                <Tooltip title="Скопировать в буфер">
                  <Button
                    type="default"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      const url = `${window.location.origin}/tournaments/${tournamentId}/broadcast?tatami=${selectedTatami}`;
                      navigator.clipboard?.writeText(url).then(() => message.success('Ссылка скопирована'));
                    }}
                  >
                    Копировать
                  </Button>
                </Tooltip>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                В OBS: Источник → Браузер → вставьте эту ссылку. На трансляции будут отображаться таймер и данные боя.
              </div>
            </NextMatchSection>
          )}

          <Button
            type="primary"
            size="small"
            icon={<FullscreenOutlined />}
            onClick={() => handleOpenBroadcast()}
            block
          >
            Открыть окно трансляции
          </Button>
        </Space>
      </Card>

      <Modal
        title="Выберите татами"
        open={isTatamiModalVisible}
        onOk={handleTatamiSelect}
        onCancel={() => setIsTatamiModalVisible(false)}
        okText="Выбрать"
        cancelText="Отмена"
      >
        <div>
          <label>Номер татами:</label>
          <InputNumber
            min={1}
            max={10}
            value={selectedTatami}
            onChange={(value) => setSelectedTatami(value || 1)}
            style={{ width: '100%', marginTop: 8 }}
          />
        </div>
      </Modal>
    </ControlContainer>
  );
};
